import { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, Alert, Paper, Grid,
  CircularProgress, Switch, FormControlLabel, Divider,
  IconButton, Tooltip, Tabs, Tab
} from '@mui/material';
import { Chat, Save, ContentCopy, Palette, Preview } from '@mui/icons-material';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function getEmpresaId() {
  try {
    const user = JSON.parse(atob(localStorage.getItem('token').split('.')[1]));
    return user.empresaId || user.id;
  } catch { return null; }
}

// Oscurece un color hex para el degradado del header (igual que el widget real)
function shadeColor(hex, pct) {
  try {
    let h = (hex || '#005eff').replace('#', '');
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    const n = parseInt(h, 16);
    let r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
    r = Math.round(r * (1 - pct)); g = Math.round(g * (1 - pct)); b = Math.round(b * (1 - pct));
    return `rgb(${r},${g},${b})`;
  } catch { return hex; }
}

// Vista previa del widget en tiempo real (refleja el widget real embebido)
function WidgetPreview({ config }) {
  const color = config.color_primario || '#005eff';
  const colorDark = shadeColor(color, 0.18);
  const grad = `linear-gradient(135deg, ${color}, ${colorDark})`;
  const empresaNombre = config.nombre_empresa || '';
  const pedirTel = config.pedir_telefono !== false;
  const telOblig = !!config.telefono_obligatorio;
  const pedirEmail = !!config.pedir_email;
  const emailOblig = !!config.email_obligatorio;

  const Campo = ({ label, placeholder }) => (
    <Box sx={{ mb: 1 }}>
      <Typography sx={{ fontSize: 10, color: '#6b7280', mb: 0.25, ml: 0.25, fontWeight: 500 }}>{label}</Typography>
      <Box sx={{ border: '1.5px solid #e5e7eb', borderRadius: 1.25, px: 1, py: 0.75, bgcolor: '#fff' }}>
        <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>{placeholder}</Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ position: 'relative', minHeight: 540, bgcolor: '#f0f0f0', borderRadius: 2, overflow: 'hidden', border: '1px solid #ddd' }}>
      <Typography variant="caption" color="text.secondary" sx={{ p: 1, display: 'block' }}>Vista previa (tu sitio web)</Typography>
      {/* Simulación del sitio */}
      <Box sx={{ px: 2, pb: 2 }}>
        {[80, 60, 70].map((w, i) => (
          <Box key={i} sx={{ height: 10, width: `${w}%`, bgcolor: '#ddd', borderRadius: 1, mb: 1 }} />
        ))}
      </Box>

      {/* Panel del widget */}
      <Box sx={{
        position: 'absolute', top: 44, right: 16, left: 16, bottom: 74, bgcolor: 'white',
        borderRadius: 3, boxShadow: '0 12px 32px rgba(0,0,0,.18)', overflow: 'hidden',
        display: 'flex', flexDirection: 'column'
      }}>
        {/* Header */}
        <Box sx={{ background: grad, p: 1.5, color: 'white', display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: 'rgba(255,255,255,.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>💬</Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {empresaNombre && (
              <Typography sx={{ color: 'rgba(255,255,255,.85)', fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.4px', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{empresaNombre}</Typography>
            )}
            <Typography sx={{ color: 'white', fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>{config.nombre_agente || 'Soporte'}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#4ade80' }} />
              <Typography sx={{ color: 'rgba(255,255,255,.9)', fontSize: 9.5 }}>En línea</Typography>
            </Box>
          </Box>
          <Typography sx={{ color: 'rgba(255,255,255,.9)', fontSize: 16, lineHeight: 1 }}>×</Typography>
        </Box>

        {/* Mensaje de bienvenida */}
        <Box sx={{ p: 1.5, bgcolor: '#f7f8fa', flexShrink: 0 }}>
          <Box sx={{ bgcolor: '#fff', borderRadius: 2, borderBottomLeftRadius: 2, p: 1, display: 'inline-block', maxWidth: '90%', boxShadow: '0 1px 2px rgba(0,0,0,.06)' }}>
            <Typography sx={{ fontSize: 11.5, color: '#1f2937' }}>{config.texto_bienvenida || '¡Hola! ¿En qué podemos ayudarte?'}</Typography>
          </Box>
        </Box>

        {/* Formulario */}
        <Box sx={{ p: 1.5, flex: 1, overflowY: 'auto', bgcolor: '#fff' }}>
          <Campo label="Tu nombre *" placeholder="Ej. María Pérez" />
          {pedirTel && <Campo label={`Tu teléfono${telOblig ? ' *' : ' (opcional)'}`} placeholder="Ej. 0991234567" />}
          {pedirEmail && <Campo label={`Tu email${emailOblig ? ' *' : ' (opcional)'}`} placeholder="tucorreo@ejemplo.com" />}
          <Typography sx={{ fontSize: 10, color: '#6b7280', mb: 0.25, ml: 0.25, fontWeight: 500 }}>¿En qué podemos ayudarte?</Typography>
          <Box sx={{ border: '1.5px solid #e5e7eb', borderRadius: 1.25, px: 1, py: 0.75, height: 34, bgcolor: '#fff', mb: 1 }}>
            <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>Escribe tu consulta...</Typography>
          </Box>
          <Box sx={{ background: grad, borderRadius: 1.25, py: 0.9, textAlign: 'center' }}>
            <Typography sx={{ color: '#fff', fontSize: 12.5, fontWeight: 600 }}>Iniciar chat</Typography>
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', py: 0.75, bgcolor: '#fff', borderTop: '1px solid #f1f2f4', flexShrink: 0 }}>
          <Typography sx={{ fontSize: 9.5, color: '#9ca3af' }}>
            Powered by <Box component="span" sx={{ color, fontWeight: 600 }}>Ai-Leth</Box>
          </Typography>
        </Box>
      </Box>

      {/* Botón flotante */}
      <Box sx={{
        position: 'absolute', bottom: 16, right: 16, width: 48, height: 48,
        borderRadius: '50%', background: grad, display: 'flex', alignItems: 'center',
        justifyContent: 'center', boxShadow: '0 6px 20px rgba(0,0,0,.25)', fontSize: 24
      }}>
        💬
      </Box>
    </Box>
  );
}

export default function ChatWidgetConfig() {
  const [config, setConfig] = useState({
    color_primario: '#005eff',
    texto_bienvenida: '¡Hola! ¿En qué podemos ayudarte?',
    nombre_agente: 'Soporte',
    activo: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);
  const [copied, setCopied] = useState(false);

  const empresaId = getEmpresaId();
  const widgetUrl = `${window.location.origin}/widget.js?empresa=${empresaId}`;
  const embedCode = `<!-- Widget de Chat Ai-leth -->
<script src="${widgetUrl}"></script>`;

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      const res = await axios.get(`${API}/api/chat-widget/config/${empresaId}`);
      if (res.data.data) setConfig(prev => ({ ...prev, ...res.data.data }));
    } catch (e) {
      setError(e.response?.data?.error || 'Error cargando configuración');
    } finally { setLoading(false); }
  }

  async function handleSave() {
    setSaving(true);
    try {
      await axios.post(`${API}/api/chat-widget/config`, config, { headers: headers() });
      setSuccess('Widget guardado. Los cambios se aplican inmediatamente.');
      setTimeout(() => setSuccess(''), 4000);
    } catch (e) {
      setError(e.response?.data?.error || 'Error guardando');
    } finally { setSaving(false); }
  }

  function copyEmbed() {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 900 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{ bgcolor: '#005eff', borderRadius: 2, p: 1, display: 'flex' }}>
          <Chat sx={{ color: 'white', fontSize: 28 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700}>Chat Widget</Typography>
          <Typography variant="body2" color="text.secondary">Instala el chat en tu sitio web con un simple script</Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Personalización" icon={<Palette />} iconPosition="start" />
        <Tab label="Código de instalación" icon={<ContentCopy />} iconPosition="start" />
      </Tabs>

      {tab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <FormControlLabel
                control={<Switch checked={config.activo} onChange={e => setConfig(p => ({ ...p, activo: e.target.checked }))} color="primary" />}
                label={<Box><Typography variant="body2" fontWeight={600}>Widget activo</Typography><Typography variant="caption" color="text.secondary">Desactiva para ocultar el chat de tu sitio</Typography></Box>}
              />
              <Divider />
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Color primario</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <input type="color" value={config.color_primario} onChange={e => setConfig(p => ({ ...p, color_primario: e.target.value }))}
                    style={{ width: 48, height: 48, border: 'none', borderRadius: 8, cursor: 'pointer', padding: 2 }} />
                  <TextField size="small" value={config.color_primario} onChange={e => setConfig(p => ({ ...p, color_primario: e.target.value }))}
                    sx={{ width: 130 }} />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {['#005eff', '#25d366', '#ff5722', '#9c27b0', '#f44336', '#000000'].map(c => (
                      <Box key={c} onClick={() => setConfig(p => ({ ...p, color_primario: c }))}
                        sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: c, cursor: 'pointer', border: config.color_primario === c ? '3px solid #fff' : 'none', boxShadow: config.color_primario === c ? `0 0 0 2px ${c}` : 'none' }} />
                    ))}
                  </Box>
                </Box>
              </Box>
              <TextField label="Nombre del agente"
                value={config.nombre_agente} onChange={e => setConfig(p => ({ ...p, nombre_agente: e.target.value }))}
                fullWidth helperText="Nombre que ve el visitante en el encabezado del chat" />
              <TextField label="Mensaje de bienvenida" multiline rows={2}
                value={config.texto_bienvenida} onChange={e => setConfig(p => ({ ...p, texto_bienvenida: e.target.value }))}
                fullWidth helperText="Primer mensaje que aparece al abrir el widget" />

              <Divider />
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>Datos que pides al visitante</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  El nombre siempre se solicita. Elige qué más pedir antes de iniciar el chat.
                </Typography>

                {/* Teléfono */}
                <FormControlLabel
                  control={<Switch checked={config.pedir_telefono !== false}
                    onChange={e => setConfig(p => ({ ...p, pedir_telefono: e.target.checked }))} color="primary" />}
                  label={<Typography variant="body2">Pedir teléfono</Typography>}
                />
                {config.pedir_telefono !== false && (
                  <FormControlLabel sx={{ ml: 3 }}
                    control={<Switch size="small" checked={!!config.telefono_obligatorio}
                      onChange={e => setConfig(p => ({ ...p, telefono_obligatorio: e.target.checked }))} color="primary" />}
                    label={<Typography variant="caption" color="text.secondary">Teléfono obligatorio</Typography>}
                  />
                )}

                {/* Email */}
                <Box sx={{ mt: 0.5 }}>
                  <FormControlLabel
                    control={<Switch checked={!!config.pedir_email}
                      onChange={e => setConfig(p => ({ ...p, pedir_email: e.target.checked }))} color="primary" />}
                    label={<Typography variant="body2">Pedir email</Typography>}
                  />
                  {config.pedir_email && (
                    <FormControlLabel sx={{ ml: 3 }}
                      control={<Switch size="small" checked={!!config.email_obligatorio}
                        onChange={e => setConfig(p => ({ ...p, email_obligatorio: e.target.checked }))} color="primary" />}
                      label={<Typography variant="caption" color="text.secondary">Email obligatorio</Typography>}
                    />
                  )}
                </Box>
              </Box>

              <Button variant="contained" size="large" startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
                onClick={handleSave} disabled={saving} sx={{ alignSelf: 'flex-start', px: 4 }}>
                {saving ? 'Guardando...' : 'Guardar cambios'}
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Vista previa en tiempo real</Typography>
            <WidgetPreview config={config} />
          </Grid>
        </Grid>
      )}

      {tab === 1 && (
        <Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            Copia el siguiente código y pégalo en el <strong>&lt;body&gt;</strong> de tu sitio web, justo antes del <strong>&lt;/body&gt;</strong>.
            El widget aparecerá automáticamente en todas las páginas donde lo incluyas.
          </Alert>
          <Paper sx={{ p: 2, bgcolor: '#1e1e1e', borderRadius: 2, position: 'relative' }}>
            <Tooltip title={copied ? '¡Copiado!' : 'Copiar código'}>
              <IconButton onClick={copyEmbed} sx={{ position: 'absolute', top: 8, right: 8, color: copied ? '#4caf50' : '#aaa' }}>
                <ContentCopy />
              </IconButton>
            </Tooltip>
            <pre style={{ color: '#d4d4d4', fontFamily: 'monospace', fontSize: 13, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              <span style={{ color: '#808080' }}>{`<!-- Widget de Chat Ai-leth -->`}</span>{'\n'}
              <span style={{ color: '#569cd6' }}>{`<script `}</span>
              <span style={{ color: '#9cdcfe' }}>src</span>
              <span style={{ color: '#d4d4d4' }}>=</span>
              <span style={{ color: '#ce9178' }}>{`"${widgetUrl}"`}</span>
              <span style={{ color: '#569cd6' }}>{`></script>`}</span>
            </pre>
          </Paper>

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>Instrucciones por plataforma</Typography>
            {[
              { plataforma: 'WordPress', instruccion: 'Apariencia → Editor de temas → footer.php → pega el script antes de </body>' },
              { plataforma: 'Wix', instruccion: 'Configuración → Avanzado → Código personalizado → Agregar código → Pie de página' },
              { plataforma: 'Shopify', instruccion: 'Tienda online → Temas → Editar código → theme.liquid → antes de </body>' },
              { plataforma: 'HTML puro', instruccion: 'Pega el script directamente en tu archivo HTML antes de la etiqueta </body>' },
            ].map(item => (
              <Box key={item.plataforma} sx={{ display: 'flex', gap: 2, mb: 1.5, p: 1.5, bgcolor: '#f9f9f9', borderRadius: 1 }}>
                <Typography variant="body2" fontWeight={700} sx={{ minWidth: 80, color: '#005eff' }}>{item.plataforma}</Typography>
                <Typography variant="body2" color="text.secondary">{item.instruccion}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
